import React, { useId, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  Bath,
  Hammer,
  HelpCircle,
  Home,
  Grid3x3,
  Paintbrush,
  PlugZap,
  Send,
  ShieldCheck,
  Sparkles,
  UtensilsCrossed,
  Warehouse,
} from 'lucide-react';

/**
 * Multi-step, tap-first estimate request.
 *
 * Replaces the long single-screen forms: the visitor taps a project type, taps a
 * timeline, and only then types contact details — so the first interaction costs
 * one tap instead of a name and a phone number.
 *
 * Submits to Netlify Forms. `formName` MUST match a hidden <form> declaration in
 * index.html or the submission 404s — Netlify only accepts posts to form names it
 * detected at deploy time.
 *
 *   variant="compact"  hero column and the popup modal
 *   variant="full"     full-width homepage section
 */

const SERVICES = [
  { label: 'Kitchen Remodeling', Icon: UtensilsCrossed },
  { label: 'Bathroom Remodeling', Icon: Bath },
  { label: 'Home Addition', Icon: Home },
  { label: 'Custom Build / Structural', Icon: Hammer },
  { label: 'Interior Finishes', Icon: Sparkles },
  { label: 'EV Charger Installation', Icon: PlugZap },
  { label: 'Flooring & Tile', Icon: Grid3x3 },
  { label: 'Roofing', Icon: Warehouse },
  { label: 'Painting', Icon: Paintbrush },
  { label: 'Other', Icon: HelpCircle },
];

const TIMELINES = [
  'As soon as possible',
  'Within 1–3 months',
  '3–6 months',
  'Just planning / exploring',
];

const formatPhone = (val) => {
  const digits = val.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits.length ? `(${digits}` : '';
  if (digits.length <= 6) return `(${digits.slice(0, 3)})-${digits.slice(3)}`;
  return `(${digits.slice(0, 3)})-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export default function QuoteWizard({
  formName,
  defaultService,
  variant = 'full',
  className = '',
  onSent,
}) {
  const compact = variant === 'compact';
  const steps = defaultService ? ['details', 'contact'] : ['service', 'details', 'contact'];
  const totalSteps = steps.length;
  const uid = useId();

  const [step, setStep] = useState(0);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    projectType: defaultService ?? '',
    timeline: '',
    description: '',
    name: '',
    phone: '',
    email: '',
    botField: '',
  });
  const navigate = useNavigate();

  const currentKey = steps[step];
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
  const canSubmit = form.name.trim() && form.phone.replace(/\D/g, '').length === 10 && emailValid;

  const next = () => setStep((s) => Math.min(s + 1, totalSteps - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const chooseService = (label) => {
    setForm((f) => ({ ...f, projectType: label }));
    next();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || sending) return;
    setSending(true);
    setError('');
    try {
      const res = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          'form-name': formName,
          name: form.name,
          phone: form.phone,
          email: form.email,
          projectType: form.projectType || 'General Inquiry',
          timeline: form.timeline,
          description: form.description,
          'bot-field': form.botField,
        }).toString(),
      });
      if (!res.ok) throw new Error(`status ${res.status}`);
      onSent?.();
      navigate('/thank-you');
    } catch {
      setError(
        "That didn't go through. Please call or text Justin at (281) 203-7688 and we'll take it from there."
      );
      setSending(false);
    }
  };

  const inputCls = `w-full bg-white/5 border border-white/15 text-white placeholder:text-white/50 rounded-sm px-4 font-body text-sm transition-colors focus:outline-none focus:border-[#C97B2E] focus-visible:ring-2 focus-visible:ring-[#C97B2E]/60 ${
    compact ? 'h-11' : 'h-12'
  }`;
  const labelCls = 'block font-body text-white/70 text-xs font-bold uppercase tracking-wider mb-1.5';
  const headingCls = `font-heading font-bold text-white ${compact ? 'text-xl' : 'text-2xl md:text-3xl'}`;
  const subCls = 'font-body text-white/70 text-sm mt-1.5';
  const primaryBtn =
    'inline-flex items-center justify-center gap-2 h-12 px-6 rounded-sm bg-[#C97B2E] text-[#0a0a0a] font-body font-bold uppercase tracking-wider text-sm transition-all duration-300 hover:brightness-110 disabled:opacity-60 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0a0a]';
  const backBtn =
    'inline-flex items-center justify-center gap-1.5 h-12 px-5 rounded-sm border border-white/25 bg-white/5 text-white font-body text-sm font-bold transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C97B2E]';

  return (
    <form
      name={formName}
      onSubmit={handleSubmit}
      data-netlify="true"
      className={className}
    >
      <input type="hidden" name="form-name" value={formName} />
      {/* Honeypot — hidden from people, tempting to bots */}
      <p className="hidden">
        <label>
          Leave this field empty
          <input
            name="bot-field"
            value={form.botField}
            tabIndex={-1}
            autoComplete="off"
            onChange={(e) => setForm({ ...form, botField: e.target.value })}
          />
        </label>
      </p>

      {/* Progress */}
      <div className={compact ? 'mb-4' : 'mb-6'}>
        <div className="mb-2 flex items-center justify-between gap-3">
          <span className="font-body text-[11px] font-bold uppercase tracking-[0.2em] text-[#C97B2E]">
            Step {step + 1} of {totalSteps}
          </span>
          {form.projectType && (
            <span className="font-body max-w-[55%] truncate text-[11px] font-semibold text-white/70">
              {form.projectType}
            </span>
          )}
        </div>
        <div
          className="h-1.5 w-full overflow-hidden rounded-full bg-white/10"
          role="progressbar"
          aria-valuemin={1}
          aria-valuemax={totalSteps}
          aria-valuenow={step + 1}
          aria-label={`Estimate request progress: step ${step + 1} of ${totalSteps}`}
        >
          <motion.div
            className="h-full rounded-full bg-[#C97B2E]"
            initial={false}
            animate={{ width: `${((step + 1) / totalSteps) * 100}%` }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
          />
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={currentKey}
          initial={{ opacity: 0, x: 16 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -16 }}
          transition={{ duration: 0.22, ease: 'easeOut' }}
        >
          {/* ── STEP 1: what kind of project ── */}
          {currentKey === 'service' && (
            <div>
              <h3 className={headingCls} id={`${uid}-service-h`}>
                What can we help you with?
              </h3>
              <p className={subCls}>Tap your project to get started — it takes about a minute.</p>
              <div
                role="group"
                aria-labelledby={`${uid}-service-h`}
                className={`mt-5 grid gap-2.5 ${compact ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-3'}`}
              >
                {SERVICES.map(({ label, Icon }) => {
                  const selected = form.projectType === label;
                  return (
                    <button
                      type="button"
                      key={label}
                      onClick={() => chooseService(label)}
                      aria-pressed={selected}
                      className={`flex flex-col items-center justify-center gap-2 rounded-sm border p-3.5 text-center transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C97B2E] ${
                        selected
                          ? 'border-[#C97B2E] bg-[#C97B2E]/10'
                          : 'border-white/15 bg-white/5 hover:border-[#C97B2E] hover:bg-[#C97B2E]/5'
                      }`}
                    >
                      <Icon className="w-6 h-6 text-[#C97B2E]" aria-hidden="true" />
                      <span className="font-body text-xs font-bold leading-tight text-white">
                        {label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── STEP 2: timeline + free text ── */}
          {currentKey === 'details' && (
            <div>
              <h3 className={headingCls}>Tell us about your project</h3>
              <p className={subCls}>A couple of quick details help us prepare an accurate estimate.</p>

              <fieldset className="mt-5 border-0 p-0 m-0">
                <legend className={labelCls}>When are you looking to start?</legend>
                <div className="grid grid-cols-2 gap-2">
                  {TIMELINES.map((t) => {
                    const selected = form.timeline === t;
                    return (
                      <button
                        type="button"
                        key={t}
                        onClick={() => setForm({ ...form, timeline: t })}
                        aria-pressed={selected}
                        className={`rounded-full border px-3 py-2.5 font-body text-xs font-bold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C97B2E] ${
                          selected
                            ? 'border-[#C97B2E] bg-[#C97B2E] text-[#0a0a0a]'
                            : 'border-white/20 bg-white/5 text-white hover:border-[#C97B2E]'
                        }`}
                      >
                        {t}
                      </button>
                    );
                  })}
                </div>
              </fieldset>

              <div className="mt-5">
                <label className={labelCls} htmlFor={`${uid}-description`}>
                  Project details <span className="font-normal normal-case">(optional)</span>
                </label>
                <textarea
                  id={`${uid}-description`}
                  name="description"
                  rows={compact ? 3 : 4}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className={`${inputCls} h-auto py-3 resize-none`}
                  placeholder="Rooms, scope, must-haves, anything we should know…"
                />
              </div>

              <div className="mt-6 flex items-center gap-3">
                {step > 0 && (
                  <button type="button" onClick={back} className={backBtn}>
                    <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Back
                  </button>
                )}
                <button type="button" onClick={next} className={`${primaryBtn} flex-1`}>
                  Continue <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </button>
              </div>
            </div>
          )}

          {/* ── STEP 3: contact details ── */}
          {currentKey === 'contact' && (
            <div>
              <h3 className={headingCls}>Where should we send your estimate?</h3>
              <p className={subCls}>Justin responds within 1 business day. No pressure, no obligation.</p>

              <div className="mt-5 space-y-4">
                <div>
                  <label className={labelCls} htmlFor={`${uid}-name`}>
                    Full name <span aria-hidden="true">*</span>
                  </label>
                  <input
                    id={`${uid}-name`}
                    name="name"
                    required
                    autoComplete="name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className={inputCls}
                    placeholder="Full name"
                  />
                </div>
                <div>
                  <label className={labelCls} htmlFor={`${uid}-phone`}>
                    Phone number <span aria-hidden="true">*</span>
                  </label>
                  <input
                    id={`${uid}-phone`}
                    name="phone"
                    required
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: formatPhone(e.target.value) })}
                    className={inputCls}
                    placeholder="(555)-555-5555"
                  />
                </div>
                <div>
                  <label className={labelCls} htmlFor={`${uid}-email`}>
                    Email address <span aria-hidden="true">*</span>
                  </label>
                  <input
                    id={`${uid}-email`}
                    name="email"
                    required
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className={inputCls}
                    placeholder="you@email.com"
                  />
                </div>
              </div>

              {error && (
                <p role="alert" className="mt-4 font-body text-sm text-[#F0A868]">
                  {error}
                </p>
              )}

              <div className="mt-6 flex items-center gap-3">
                <button type="button" onClick={back} className={backBtn}>
                  <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Back
                </button>
                <button type="submit" disabled={sending || !canSubmit} className={`${primaryBtn} flex-1`}>
                  <Send className="w-4 h-4" aria-hidden="true" />
                  {sending ? 'Sending…' : 'Get My Free Estimate'}
                </button>
              </div>

              <p className="mt-4 flex items-center justify-center gap-1.5 text-center font-body text-xs text-white/60">
                <ShieldCheck className="w-3.5 h-3.5 shrink-0 text-[#C97B2E]" aria-hidden="true" />
                Your info stays private — we never share or sell it.
              </p>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </form>
  );
}
